import { browser } from '$app/environment';

export type Theme = 'hearth' | 'galactic' | 'wizardry' | 'overgrown' | 'outbreak' | 'catchers' | 'noir' | 'dark' | 'light';

const THEME_MIGRATIONS: Record<string, Theme> = {
	'travellers-rest': 'hearth',
	starwars: 'galactic',
	harrypotter: 'wizardry',
	lastofus: 'overgrown',
	walkingdead: 'outbreak',
	breakingbad: 'dark',
	crystal: 'dark',
	bubblegum: 'dark',
	pokemon: 'catchers',
	flat: 'dark'
};
const VALID_THEMES = new Set<Theme>(['hearth', 'galactic', 'wizardry', 'overgrown', 'outbreak', 'catchers', 'noir', 'dark', 'light']);

function migrateTheme(raw: unknown): Theme {
	if (typeof raw !== 'string') return 'dark';
	if (VALID_THEMES.has(raw as Theme)) return raw as Theme;
	return THEME_MIGRATIONS[raw] ?? 'dark';
}

export type Settings = {
	soundEnabled: boolean;
	soundVolume: number;
	hapticsEnabled: boolean;
	theme: Theme;
	disableVideo: boolean;
	dimBackground: boolean;
	keyboardTextLabels: boolean;
	username: string;
	avatar: string;
	joinedDate: string;
	favorites: string[];
	gameOrder: string[];
	seenReorderHint: boolean;
	seenFavoriteHint: boolean;
	seenThemeHint: boolean;
	seenWelcomeModal: boolean;
	earnedBadges: string[];
	featuredBadge: string;
	shareGameStatus: boolean;
	friendOnlineToasts: boolean;
	appearOffline: boolean;
};

const STORAGE_KEY = 'bubble-games-settings';
const DEFAULT_SETTINGS: Settings = {
	soundEnabled: true,
	soundVolume: 0.8,
	hapticsEnabled: true,
	theme: 'dark',
	disableVideo: false,
	dimBackground: false,
	keyboardTextLabels: false,
	username: '',
	avatar: '',
	joinedDate: '',
	favorites: [],
	gameOrder: [],
	seenReorderHint: false,
	seenFavoriteHint: false,
	seenThemeHint: false,
	seenWelcomeModal: false,
	earnedBadges: [],
	featuredBadge: '',
	shareGameStatus: true,
	friendOnlineToasts: true,
	appearOffline: false
};

export function readSettings(): Settings {
	if (!browser) return DEFAULT_SETTINGS;

	try {
		const raw = window.localStorage.getItem(STORAGE_KEY);
		if (!raw) return DEFAULT_SETTINGS;
		const parsed = JSON.parse(raw);
		return {
			soundEnabled: parsed.soundEnabled !== false,
			soundVolume: typeof parsed.soundVolume === 'number' ? Math.max(0, Math.min(1, parsed.soundVolume)) : 0.8,
			hapticsEnabled: parsed.hapticsEnabled !== false,
			theme: migrateTheme(parsed.theme),
			disableVideo: parsed.disableVideo === true,
			dimBackground: parsed.dimBackground === true,
			keyboardTextLabels: parsed.keyboardTextLabels === true,
			username: typeof parsed.username === 'string' ? parsed.username : '',
			avatar: typeof parsed.avatar === 'string' ? parsed.avatar : '',
			joinedDate: typeof parsed.joinedDate === 'string' ? parsed.joinedDate : '',
			favorites: Array.isArray(parsed.favorites) ? parsed.favorites.filter((v: unknown): v is string => typeof v === 'string') : [],
			gameOrder: Array.isArray(parsed.gameOrder) ? parsed.gameOrder.filter((v: unknown): v is string => typeof v === 'string') : [],
			seenReorderHint: parsed.seenReorderHint === true,
			seenFavoriteHint: parsed.seenFavoriteHint === true,
			seenThemeHint: parsed.seenThemeHint === true,
			seenWelcomeModal: parsed.seenWelcomeModal === true,
			earnedBadges: Array.isArray(parsed.earnedBadges) ? parsed.earnedBadges.filter((v: unknown): v is string => typeof v === 'string') : [],
			featuredBadge: typeof parsed.featuredBadge === 'string' ? parsed.featuredBadge : '',
			shareGameStatus: parsed.shareGameStatus !== false,
			friendOnlineToasts: parsed.friendOnlineToasts !== false,
			appearOffline: parsed.appearOffline === true
		};
	} catch {
		return DEFAULT_SETTINGS;
	}
}

export function writeSettings(settings: Settings) {
	if (!browser) return;
	window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
}

export function applyTheme(theme: Theme) {
	if (!browser) return;
	document.documentElement.dataset.theme = theme;
}

export function applyDimBackground(dim: boolean) {
	if (!browser) return;
	if (dim) {
		document.documentElement.dataset.dimBackground = 'true';
	} else {
		delete document.documentElement.dataset.dimBackground;
	}
}
